import React from 'react';
import useCheckoutState from '../../hooks/useCheckoutState';
import { PaymentElement as StripePaymentElement } from '@stripe/react-stripe-js';
import getBillingDetailsFromFields from '../../lib/getBillingDetailsFromFields';
import getFormFieldValues from '../../lib/getFormFieldValues';
import { findFieldByType } from '../../lib/findFieldByType';
import logException from '../../services/logException';

const PAYMENT_FORM_LOAD_ERROR = "Payment form couldn't be loaded. Refresh the page to try again.";

// Stripe's api_connection_error copy tells the buyer to check their internet connection,
// which is rarely the cause and gives them nothing to do. A refresh almost always works.
const getLoadErrorMessage = error => {
  if (!error?.message) return PAYMENT_FORM_LOAD_ERROR;

  const isConnectionError =
    error.type === 'api_connection_error' ||
    /connecting to our payments provider/i.test(error.message);

  return isConnectionError ? PAYMENT_FORM_LOAD_ERROR : error.message;
};

const PaymentElement = ({ onReady }) => {
  const { setFormError, seller, setSelectedPaymentMethod, page } = useCheckoutState();
  const { form } = useCheckoutState();

  const { fields } = getFormFieldValues(form.fields, page.fields);
  const billingDetails = getBillingDetailsFromFields(fields);

  const hasRequiredNameField = !!findFieldByType(fields, 'name')?.required;
  const hasRequiredFirstNameField = !!findFieldByType(fields, 'first-name')?.required;
  const hasRequiredLastNameField = !!findFieldByType(fields, 'last-name')?.required;

  const shouldHideNameField =
    hasRequiredNameField ||
    hasRequiredFirstNameField ||
    hasRequiredLastNameField ||
    billingDetails.name;

  return (
    <div className="cp_stripe-payment-element">
      <StripePaymentElement
        onLoadError={({ error }) => {
          logException(error?.message || PAYMENT_FORM_LOAD_ERROR);
          setFormError(getLoadErrorMessage(error));
        }}
        onReady={onReady}
        onChange={event => {
          setFormError(null);
          setSelectedPaymentMethod(event.value.type);
        }}
        options={{
          defaultValues: {
            billingDetails,
          },
          fields: {
            billingDetails: {
              email: 'never',
              ...(shouldHideNameField && { name: 'never' }),
              ...(billingDetails.phone && { phone: 'never' }),
              ...(billingDetails.address && {
                address: {
                  ...(billingDetails.address.line1 && { line1: 'never' }),
                  ...(billingDetails.address.line2 && { line2: 'never' }),
                  ...(billingDetails.address.city && { city: 'never' }),
                  ...(billingDetails.address.state && { state: 'never' }),
                  ...(billingDetails.address.postal_code && {
                    postal_code: 'never',
                  }),
                  ...(billingDetails.address.country && { country: 'never' }),
                },
              }),
            },
          },

          ...(seller.displayName && {
            business: {
              name: seller.displayName,
            },
          }),
        }}
      />
    </div>
  );
};

export default PaymentElement;
